import { useMemo, useState } from "react";

import type { AdminUser, AuditEntry, Customer } from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { auditActionLabel, formatDateTime } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import { useScope } from "../store/auth.js";

const PAGE_SIZE = 25;
const ENTITY_TYPES = [
  "customer",
  "event",
  "operator",
  "admin_user",
  "attendee",
  "event_operator",
  "meetpoint_config",
  "meetpoint_sync"
];

interface AuditRefData {
  users: AdminUser[];
  customers: Customer[];
}

/** Audit log — filterable table with expandable diff rows (build plan P2-T11). */
export function AuditLogPage(): JSX.Element {
  const { isPortalAdmin } = useScope();
  const [userFilter, setUserFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const refs = useAsync<AuditRefData>(async () => {
    const users = await api.listAdminUsers();
    const customers = isPortalAdmin ? await api.listCustomers() : [];
    return { users, customers };
  }, [isPortalAdmin]);

  const audit = useAsync(
    () =>
      api.listAudit({
        admin_user_id: userFilter || undefined,
        entity_type: entityFilter || undefined,
        customer_id: isPortalAdmin && customerFilter ? customerFilter : undefined,
        page,
        limit: PAGE_SIZE
      }),
    [userFilter, entityFilter, customerFilter, page, isPortalAdmin]
  );

  const userName = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of refs.data?.users ?? []) {
      map.set(u.id, u.name);
    }
    return map;
  }, [refs.data?.users]);

  const customerName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of refs.data?.customers ?? []) {
      map.set(c.id, c.name);
    }
    return map;
  }, [refs.data?.customers]);

  const totalPages = audit.data
    ? Math.max(1, Math.ceil(audit.data.total / PAGE_SIZE))
    : 1;

  return (
    <>
      <PageHeader
        title="Audit log"
        description="A record of every administrative change."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="audit-user" className="text-muted-foreground">
            User
          </Label>
          <Select
            id="audit-user"
            className="w-48"
            value={userFilter}
            onChange={(e) => {
              setUserFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {(refs.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="audit-entity" className="text-muted-foreground">
            Entity
          </Label>
          <Select
            id="audit-entity"
            className="w-44"
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        {isPortalAdmin ? (
          <div className="flex items-center gap-2">
            <Label htmlFor="audit-customer" className="text-muted-foreground">
              Customer
            </Label>
            <Select
              id="audit-customer"
              className="w-48"
              value={customerFilter}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              {(refs.data?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      <Card>
        {audit.loading ? (
          <LoadingState />
        ) : audit.error ? (
          <ErrorState message={audit.error} onRetry={audit.reload} />
        ) : !audit.data || audit.data.data.length === 0 ? (
          <EmptyState
            title="No audit entries"
            description="Administrative changes will appear here."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  {isPortalAdmin ? <TableHead>Customer</TableHead> : null}
                  <TableHead className="text-right">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.data.data.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    userName={userName.get(entry.admin_user_id ?? "") ?? "—"}
                    customerName={
                      entry.customer_id
                        ? (customerName.get(entry.customer_id) ?? "—")
                        : "Portal"
                    }
                    showCustomer={isPortalAdmin}
                    expanded={expanded === entry.id}
                    onToggle={() =>
                      setExpanded((cur) => (cur === entry.id ? null : entry.id))
                    }
                  />
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border p-3 text-sm">
              <span className="text-muted-foreground">
                {audit.data.total} entr{audit.data.total === 1 ? "y" : "ies"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

function AuditRow({
  entry,
  userName,
  customerName,
  showCustomer,
  expanded,
  onToggle
}: {
  entry: AuditEntry;
  userName: string;
  customerName: string;
  showCustomer: boolean;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const hasDiff = entry.diff && Object.keys(entry.diff).length > 0;
  const colSpan = showCustomer ? 6 : 5;
  return (
    <>
      <TableRow>
        <TableCell className="text-muted-foreground">
          {formatDateTime(entry.created_at)}
        </TableCell>
        <TableCell>{userName}</TableCell>
        <TableCell>{auditActionLabel(entry.action)}</TableCell>
        <TableCell className="text-muted-foreground">{entry.entity_type}</TableCell>
        {showCustomer ? (
          <TableCell className="text-muted-foreground">{customerName}</TableCell>
        ) : null}
        <TableCell className="text-right">
          {hasDiff ? (
            <Button size="sm" variant="ghost" onClick={onToggle}>
              {expanded ? "Hide" : "View diff"}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && hasDiff ? (
        <TableRow>
          <TableCell colSpan={colSpan} className="bg-secondary/40">
            <pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs">
              {JSON.stringify(entry.diff, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
